import format from 'date-fns/format';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import logo from '@assets/footer-logo.svg';

import { externalLinks } from '@utils/externalLinks';
import { BROWSER_BALANCE, BROWSER_FEEDBACK } from '@utils/links';

import Lang from '@shared/Lang/Lang';

import { footerLinksConfig } from './config';
import { build, version } from '../../constants/version';

import styles from './Footer.module.scss';

export default function DesktopFooter() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { about, dashboard, promo } = useMemo(() => {
    if (pathname.includes('/farm')) {
      return footerLinksConfig.farm;
    }

    if (pathname.includes('/pool')) {
      return footerLinksConfig.pool;
    }

    return footerLinksConfig.browser;
  }, [pathname]);
  return (
    <div className={styles.footer} data-no-theme="true">
      <div className={styles.footer__content}>
        <div className={styles.footer__logo}>
          <img src={logo} alt="Cryptotab Logo" />
          <Lang footer forMobile />
        </div>
        <div className={styles.footer__menu}>
          <ul className={styles.menu}>
            <li className={styles.menu__label}>{t('General')}</li>
            <li className={styles.menu__item}>
              <Link to={{ pathname: dashboard }}>{t('Dashboard')}</Link>
            </li>
            <li className={styles.menu__item}>
              <Link to={{ pathname: promo }}>{t('Links & Promo')}</Link>
            </li>
            <li className={styles.menu__item}>
              <Link to={{ pathname: about }}>{t('About')}</Link>
            </li>
            <li className={styles.menu__item}>
              <a href={BROWSER_FEEDBACK}>{t('Support')}</a>
            </li>
          </ul>
          <ul className={styles.menu}>
            <li className={styles.menu__label}>{t('Additional')}</li>
            <li className={styles.menu__item}>
              <a href={externalLinks.ncw} target="_blank" rel="noreferrer">
                NC Wallet
              </a>
            </li>
            <li className={styles.menu__item}>
              <a href="https://cryptobrowser.site/affilate/terms/" target="_blank" rel="noreferrer">
                {t('Terms of Use')}
              </a>
            </li>
            <li className={styles.menu__item}>
              <a href="https://cryptobrowser.site/privacy/" target="_blank" rel="noreferrer">
                {t('Privacy Policy')}
              </a>
            </li>
            <li className={styles.menu__item}>
              <a href={BROWSER_BALANCE}>{t('Payments Journal')}</a>
            </li>
            <li className={styles.menu__item}>
              <a href="https://cryptotabbrowser.com/">{t('Download Browser')}</a>
            </li>
            <li className={styles.menu__item}>
              <a href="https://cryptobrowser.site/news/">{t('Tips and News')}</a>
            </li>
            <li className={styles.menu__item}>
              <a href="https://cryptobrowser.site/faq/">{t('FAQ')}</a>
            </li>
          </ul>
          <ul className={styles.menu}>
            <li className={styles.menu__label}>{t('CryptoTab Family')}</li>
            <li className={styles.menu__item}>
              <a href="https://cryptobrowser.site/">
                CryptoTab <b>Browser</b>
              </a>
            </li>

            <li className={styles.menu__item}>
              <a
                className="js-gplay"
                target="_top"
                href="https://play.app.goo.gl/?link=https%3A//play.google.com/store/apps/details%3Fid%3Dmax.cryptotab.android%26referrer%3Dutm_source%253Dlanding%2526utm_medium%253Dfooter%2526utm_content%253De85b0ff3-1392-4ee0-b56a-61a824984ba3"
              >
                CryptoTab <b>for Android</b>
              </a>
              <span className={`${styles.bonus} ${styles.bonus_purple}`}>MAX</span>
            </li>
            <li className={styles.menu__item}>
              <a
                className="js-gplay"
                target="_top"
                href="https://play.app.goo.gl/?link=https%3A%2F%2Fplay.google.com%2Fstore%2Fapps%2Fdetails%3Fid%3Dpro.cryptotab.android%26referrer%3Dutm_source%253Dlanding%2526utm_medium%253Dfooter%2526utm_content%253De85b0ff3-1392-4ee0-b56a-61a824984ba3"
              >
                CryptoTab <b>for Android</b>
              </a>
              <span className={`${styles.bonus} ${styles.bonus_orange}`}>Pro</span>
            </li>
            <li className={styles.menu__item}>
              <a
                className="js-gplay"
                target="_top"
                href="https://play.app.goo.gl/?link=https%3A%2F%2Fplay.google.com%2Fstore%2Fapps%2Fdetails%3Fid%3Dlite.cryptotab.android%26referrer%3Dutm_source%253Daffiliate%2526utm_medium%253Dfooter%2526utm_content%253De85b0ff3-1392-4ee0-b56a-61a824984ba3"
              >
                CryptoTab <b>for Android</b>
              </a>
              <span className={`${styles.bonus} ${styles.bonus_turq}`}>Lite</span>
            </li>
            <li className={styles.menu__item}>
              <a href="https://itunes.apple.com/app/cryptotab-browser-pro/id1524974223/">
                CryptoTab <b>for iOS</b>
              </a>
            </li>
            <li className={styles.menu__item}>
              <a target="_blank" rel="noreferrer" href="https://cryptotab.farm/">
                CryptoTab <b>Farm</b>
              </a>
              <span className={`${styles.bonus} ${styles.bonus_red}`}>New</span>
            </li>
            <li className={styles.menu__item}>
              <a target="_blank" rel="noreferrer" href="https://ctags.app">
                CTags
              </a>
              <span className={`${styles.bonus} ${styles.bonus_red}`}>New</span>
            </li>
            <li className={styles.menu__item}>
              <a target="_blank" rel="noreferrer" href="https://ct-vpn.com/en/landing_full/">
                CT VPN
              </a>
            </li>
            <li className={styles.menu__item}>
              <a target="_blank" rel="noreferrer" href="https://market.ctnft.net/?utm_source=ctb-app">
                CT Store
              </a>
              <span className={`${styles.bonus} ${styles.bonus_red}`}>New</span>
            </li>
            <li className={styles.menu__item}>
              <a target="_blank" rel="noreferrer" href="https://cb.click">
                CB.click
              </a>
            </li>
            <li className={styles.menu__item}>
              <a target="_blank" rel="noreferrer" href="https://start.cryptobrowser.site/">
                CryptoTab <b>START</b>
              </a>
              <span className={`${styles.bonus} ${styles.bonus_blue}`}>BONUS</span>
            </li>
            <li className={styles.menu__item}>
              <a target="_blank" rel="noreferrer" href="https://ctabs.net/">
                CTabs
              </a>
              <span className={`${styles.bonus} ${styles.bonus_blue}`}>BONUS</span>
            </li>
          </ul>
          <ul className={styles.menu}>
            <li className={styles.menu__label}>{t('Stay Connected')}</li>
            <li className={`${styles.menu__item} ${styles.menu__item_join}`}>
              <div className={styles.sociallinks}>
                <a
                  href="https://discord.com/invite/cryptotab-community-997079413013352529"
                  target="_blank"
                  rel="noreferrer"
                  className={styles.sociallink}
                >
                  <svg width="23" height="18" viewBox="0 0 23 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M19.5001 1.52023C22.0977 5.44291 23.3805 9.86758 22.901 14.9614C22.899 14.983 22.888 15.0027 22.8708 15.0157C20.9037 16.4993 18.9979 17.3996 17.1188 17.9967C17.1042 18.0013 17.0885 18.001 17.074 17.996C17.0595 17.991 17.0469 17.9814 17.038 17.9687C16.6039 17.3483 16.2094 16.6942 15.8638 16.0073C15.844 15.9669 15.8621 15.9181 15.903 15.9022C16.5294 15.6598 17.1251 15.3692 17.6981 15.0252C17.7432 14.998 17.7461 14.9316 17.7044 14.8997C17.5828 14.807 17.4623 14.7095 17.3471 14.6121C17.3255 14.594 17.2965 14.5905 17.272 14.6026C13.5521 16.367 9.47705 16.367 5.7131 14.6026C5.68867 14.5914 5.65963 14.5952 5.63864 14.6129C5.52364 14.7104 5.40289 14.807 5.28243 14.8997C5.24074 14.9316 5.24419 14.998 5.28962 15.0252C5.8626 15.3627 6.4583 15.6598 7.0839 15.9034C7.12444 15.9193 7.1437 15.9669 7.12358 16.0073C6.78548 16.6951 6.39103 17.3491 5.94885 17.9695C5.92959 17.9946 5.89797 18.0062 5.86807 17.9967C3.99788 17.3996 2.09205 16.4993 0.124977 15.0157C0.108589 15.0027 0.0968019 14.9821 0.0950769 14.9605C-0.305697 10.5544 0.511089 6.09315 3.49275 1.51935C3.49993 1.50724 3.51086 1.49779 3.52351 1.49218C4.99062 0.800604 6.56238 0.291813 8.20515 0.00124481C8.23505 -0.00347988 8.26495 0.0106942 8.28048 0.0378612C8.48345 0.406978 8.71546 0.880333 8.87244 1.26717C10.604 0.995497 12.3627 0.995497 14.1305 1.26717C14.2875 0.888601 14.5115 0.406978 14.7136 0.0378612C14.7208 0.0243839 14.7319 0.0135867 14.7454 0.00701094C14.759 0.000435179 14.7742 -0.0015829 14.7889 0.00124481C16.4325 0.292699 18.0043 0.80149 19.4702 1.49218C19.4832 1.49779 19.4938 1.50724 19.5001 1.52023V1.52023ZM9.75276 9.89771C9.77087 8.59517 8.84627 7.51735 7.68564 7.51735C6.53449 7.51735 5.61881 8.58572 5.61881 9.89771C5.61881 11.2094 6.5526 12.2778 7.68564 12.2778C8.83707 12.2778 9.75276 11.2094 9.75276 9.89771V9.89771ZM17.3951 9.89771C17.4132 8.59517 16.4886 7.51735 15.3282 7.51735C14.1768 7.51735 13.2611 8.58572 13.2611 9.89771C13.2611 11.2094 14.1949 12.2778 15.3282 12.2778C16.4886 12.2778 17.3951 11.2094 17.3951 9.89771V9.89771Z"
                      fill="#5865F2"
                      fillOpacity="0.98"
                    ></path>
                  </svg>
                </a>
                <a
                  href="https://www.facebook.com/CryptoTabFamilyOfficial"
                  target="_blank"
                  rel="noreferrer"
                  className={`${styles.sociallink} ${styles.sociallink_fb}`}
                >
                  <i></i>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M19.998 10.0611C19.998 4.50451 15.5213 0 9.99902 0C4.47671 0 0 4.50451 0 10.0611C0 15.0829 3.6565 19.2452 8.43668 20V12.9694H5.89786V10.0611H8.43668V7.84452C8.43668 5.32296 9.92946 3.93012 12.2135 3.93012C13.3074 3.93012 14.4517 4.12663 14.4517 4.12663V6.60261H13.1909C11.9487 6.60261 11.5614 7.37816 11.5614 8.17381V10.0611H14.3345L13.8912 12.9694H11.5614V20C16.3415 19.2452 19.998 15.0829 19.998 10.0611"
                      fill="white"
                    ></path>
                  </svg>
                </a>

                <a
                  href="https://t.me/CryptoTabChannel"
                  target="_blank"
                  rel="noreferrer"
                  className={`${styles.sociallink} ${styles.sociallink_telegram}`}
                >
                  <svg width="20px" height="17px" viewBox="0 0 20 17" version="1.1" xmlns="http://www.w3.org/2000/svg">
                    <defs></defs>
                    <g id="Page-1" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                      <g id="getcryptotab--chat-icons" transform="translate(-771.000000, -49.000000)">
                        <g id="first" transform="translate(-100.000000, -61.000000)">
                          <g id="social" transform="translate(726.000000, 109.000000)">
                            <path
                              d="M161.907864,16.9314884 L164.926378,2.58440214 C165.073622,1.98970427 165,1.54368086 164.705511,1.24633193 C164.411022,0.948982988 164.116533,0.948982988 163.674799,1.09765746 L145.931827,8.01102025 C145.563715,8.15969472 145.269226,8.30836919 145.121981,8.53138089 C144.974737,8.7543926 144.974737,8.90306707 145.048359,9.05174153 C145.121981,9.200416 145.342848,9.34909047 145.71096,9.42342771 L150.275542,10.8358352 L160.803529,4.14548407 C161.098019,3.92247236 161.318885,3.92247236 161.46613,3.9968096 C161.539752,4.07114683 161.539752,4.14548407 161.392508,4.29415853 L152.852322,12.1739054 L152.484211,16.9314884 C152.7787,16.9314884 153.073189,16.7828139 153.4413,16.485465 L155.649969,14.3296852 L160.214551,17.749198 C161.171641,18.2695586 161.686997,17.9722097 161.907864,16.9314884 Z"
                              id="Path"
                            ></path>
                          </g>
                        </g>
                      </g>
                    </g>
                  </svg>
                </a>
                <a
                  href="https://twitter.com/CryptoTabnet"
                  target="_blank"
                  rel="noreferrer"
                  className={`${styles.sociallink} ${styles.sociallink_twitter}`}
                >
                  <svg width="18" height="15" xmlns="http://www.w3.org/2000/svg">
                    <path
                      id="getcryptotab--chat-icons"
                      d="M1.057.644s3.077 3.86 7.765 4.092c0 0-.957-2.56 1.903-4.315 0 0 2.324-1.378 4.566.853 0 0 1.012-.05 2.23-.955 0 0 .18.833-1.41 2.172 0 0 .885-.072 1.889-.736 0 0-.191 1.146-1.829 1.979 0 0 .498 9.068-8.27 11.053 0 0-4.636 1.056-7.901-1.53 0 0 3.464.364 5.304-1.584 0 0-2.31.218-3.446-2.531 0 0 1.155.2 1.624-.182 0 0-2.436.018-2.977-3.66 0 0 .992.51 1.642.418 0 0-2.757-1.718-1.09-5.074"
                      fill="#FFF"
                      fillRule="evenodd"
                    ></path>
                  </svg>
                </a>
                <a
                  href="https://www.instagram.com/cryptotabme/"
                  target="_blank"
                  rel="noreferrer"
                  className={`${styles.sociallink} ${styles.sociallink_instagram}`}
                >
                  <svg width="20px" height="20px" viewBox="0 0 20 20" version="1.1" xmlns="http://www.w3.org/2000/svg">
                    <g id="Page-1" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                      <g
                        id="ctb_instagram-youtube"
                        transform="translate(-1110.000000, -51.000000)"
                        fill="#494949"
                        fillRule="nonzero"
                      >
                        <g id="head" transform="translate(40.000000, 30.000000)">
                          <g id="social" transform="translate(882.000000, 20.000000)">
                            <g id="instagram" transform="translate(188.000000, 1.000000)">
                              <path
                                d="M1.91145833,5.79828774 L1.91145833,14.1841341 C1.91145833,16.3253435 3.62515745,18.0595703 5.74047844,18.0595703 L14.1241049,18.0595703 C16.2379466,18.0595703 17.953125,16.3238517 17.953125,14.1841341 L17.953125,5.79828774 C17.953125,3.65707835 16.2394259,1.92285156 14.1241049,1.92285156 L5.74047844,1.92285156 C3.62663676,1.92285156 1.91145833,3.65857021 1.91145833,5.79828774 Z M0.0885416667,5.79828774 C0.0885416667,2.6390024 2.62007905,0.0771484375 5.74047844,0.0771484375 L14.1241049,0.0771484375 C17.2459848,0.0771484375 19.7760417,2.63751176 19.7760417,5.79828774 L19.7760417,14.1841341 C19.7760417,17.3434195 17.2445043,19.9052734 14.1241049,19.9052734 L5.74047844,19.9052734 C2.61859853,19.9052734 0.0885416667,17.3449101 0.0885416667,14.1841341 L0.0885416667,5.79828774 Z M9.95833333,15.3173828 C7.09623268,15.3173828 4.77604167,12.9681894 4.77604167,10.0703125 C4.77604167,7.17243559 7.09623268,4.82324219 9.95833333,4.82324219 C12.820434,4.82324219 15.140625,7.17243559 15.140625,10.0703125 C15.140625,12.9681894 12.820434,15.3173828 9.95833333,15.3173828 Z M9.95833333,13.4716797 C11.8136649,13.4716797 13.3177083,11.9488357 13.3177083,10.0703125 C13.3177083,8.19178927 11.8136649,6.66894531 9.95833333,6.66894531 C8.10300175,6.66894531 6.59895833,8.19178927 6.59895833,10.0703125 C6.59895833,11.9488357 8.10300175,13.4716797 9.95833333,13.4716797 Z M15.1927083,5.95703125 C14.5023524,5.95703125 13.9427083,5.39039164 13.9427083,4.69140625 C13.9427083,3.99242086 14.5023524,3.42578125 15.1927083,3.42578125 C15.8830643,3.42578125 16.4427083,3.99242086 16.4427083,4.69140625 C16.4427083,5.39039164 15.8830643,5.95703125 15.1927083,5.95703125 Z"
                                id="instagram_up"
                              ></path>
                            </g>
                          </g>
                        </g>
                      </g>
                    </g>
                  </svg>
                </a>
                <a
                  href="https://www.youtube.com/c/CryptoTabBrowser"
                  target="_blank"
                  rel="noreferrer"
                  className={`${styles.sociallink} ${styles.sociallink_youtube}`}
                >
                  <svg width="20px" height="14px" viewBox="0 0 20 14" version="1.1" xmlns="http://www.w3.org/2000/svg">
                    <g id="Page-1" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                      <g id="ctb_instagram-youtube" transform="translate(-1038.000000, -702.000000)" fill="#FFFFFF">
                        <g id="footer" transform="translate(0.000000, 570.000000)">
                          <g id="Group" transform="translate(897.000000, 95.000000)">
                            <g id="social" transform="translate(3.000000, 34.000000)">
                              <g id="youtube_icon" transform="translate(138.000000, 3.000000)">
                                <path
                                  d="M9.98451161,0 C14.7021717,0 16.9409606,0.2252828 16.9409606,0.2252828 C16.9409606,0.2252828 18.6569011,0.284647225 19.3276204,1.56051046 C19.3276204,1.56051046 19.9983397,2.48627459 19.9983397,7 C19.9983397,7 20.0387416,9.79432782 19.7209449,11.2799013 C19.4031007,12.7654276 18.7012957,13.1992881 18.7012957,13.1992881 C18.7012957,13.1992881 18.2510758,13.6331014 17.2579492,13.7514056 C16.2648227,13.8697569 13.0446,14 9.98451161,14 C9.98451161,14 4.38247739,13.9968383 2.49551791,13.6871806 C0.608558439,13.3775701 0.343474356,11.6280656 0.343474356,11.6280656 C0.343474356,11.6280656 -0.429342946,7.19932856 0.343474356,2.37688927 C0.343474356,2.37688927 0.567158361,0.45061279 2.82177524,0.2252828 C2.82177524,0.2252828 5.26680397,0 9.98451161,0 Z M13.4065934,6.890625 L7.91208791,4.15625 L7.91208791,9.625 L13.4065934,6.890625 Z"
                                  id="youtube_icon-up"
                                ></path>
                              </g>
                            </g>
                          </g>
                        </g>
                      </g>
                    </g>
                  </svg>
                </a>
              </div>
            </li>

            <li className={styles.menu__item} style={{ marginTop: 20 }}>
              <div className={styles.feedbacklink}>
                {t('Contact support')} <Link to={{ pathname: BROWSER_FEEDBACK }}>{t('Here')}</Link>
              </div>
            </li>
            <li className={styles.menu__item}>
              <div className={styles.otherinquiries}>
                {t('Other Inquiries')}: <a href="mailto:contactus@cryptobrowser.site">contactus@cryptobrowser.site</a>
              </div>
            </li>
          </ul>
        </div>
      </div>
      <div className={styles.footer__bottom}>
        <div className={styles.footer__content}>
          <div className={styles.footer__copyright}>
            © {format(new Date(), 'yyyy')}.{' '}
            <span>{t('All rights reserved.')} CryptoCompany OÜ, Rebase tn 1, Tartu 50104, Estonia</span>
            &nbsp; (Build: {version + '.' + build})
          </div>

          <Lang footer />
        </div>
      </div>
    </div>
  );
}
